import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";

import { ScriptLoaderService } from "../../script-loader-service";

declare const Plaid: {
  create: (config: PlaidLinkConfig) => PlaidLinkHandler;
};

interface PlaidLinkConfig {
  token: string;
  onSuccess: (publicToken: string, metadata: unknown) => void;
  onExit: (err: unknown, metadata: unknown) => void;
  receivedRedirectUri?: string;
}

interface PlaidLinkHandler {
  open: () => void;
  destroy: () => void;
}

@Injectable({
  providedIn: "root",
})
export class PlaidLinkService {
  private renderer: Renderer2;

  constructor(
    private scriptLoader: ScriptLoaderService,
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Ensure the Plaid Link SDK script is loaded, then open Link with the given token.
   * Returns a Promise that resolves with { publicToken, metadata } on onSuccess,
   * or rejects with { error, metadata } on onExit with an error.
   * Resolves with null on clean user exit (no error).
   */
  openLink(
    linkToken: string,
    receivedRedirectUri?: string,
  ): Promise<{ publicToken: string; metadata: unknown } | null> {
    return new Promise((resolve, reject) => {
      const init = () => {
        const config: PlaidLinkConfig = {
          token: linkToken,
          onSuccess: (publicToken, metadata) => {
            handler.destroy();
            resolve({ publicToken, metadata });
          },
          onExit: (err, metadata) => {
            handler.destroy();
            if (err) {
              reject({ error: err, metadata });
            } else {
              resolve(null); // clean exit
            }
          },
        };
        if (receivedRedirectUri !== undefined) {
          config.receivedRedirectUri = receivedRedirectUri;
        }
        const handler = Plaid.create(config);
        handler.open();
      };

      // If already loaded, init immediately; else load the SDK and wait for it.
      if (typeof Plaid !== "undefined") {
        init();
        return;
      }

      const existingScript = this.document.getElementById(
        this.scriptLoader.elementId,
      ) as HTMLScriptElement | null;
      const scriptEl =
        existingScript ??
        this.scriptLoader.loadJsScript(this.renderer, this.scriptLoader.linkInitializeUri);
      scriptEl.addEventListener("load", init, { once: true });
      scriptEl.addEventListener("error", () => reject(new Error("PLAID_SDK_LOAD_FAILED")), {
        once: true,
      });
    });
  }
}
